import React, { useContext, useEffect, useState } from 'react';
import { useBoolean, useToast } from '@chakra-ui/react';
import { UiKit, Text, Button } from '@datavant/uikit';
import { DigitalFulfillmentLogo } from '../components/ProviderTools/DigitalFulfillmentLogo';
import { SelectBoxWithHint } from '@datavant/idsb-shared/components/forms/SelectBoxWithHint';
import { InputWithHint } from '@datavant/idsb-shared/components/forms/InputWithHint';
import useUser from '../lib/useUser';
import { trpc, tRPCProxyClient } from '../utils/trpc';
import { EmrEnvironment } from '../generated/tenant-api';
import { NonEmbeddedImplementationStatus } from '../generated/request-manager-api';
import { useLocalStorage } from 'usehooks-ts';
import { sendSlackAlert, SlackChannelEnum } from '../components/api/slack';
import { AddressAssociationFeedbackType } from '../server/apiHelpers/provider-feedback/types';
import { AgreementModalContext } from '../lib/contexts/AgreementModalContext';

const EmrEnvironmentHelpText = {
    CLOUD: 'Do you login via the internet on a website with a username/password? aka patient data hosted with the vendor.',
    ON_PREMISE:
        'Does the practice locally host the EMR servers and do you have a dedicated IT partner that manages this?',
    NOT_SURE: '',
};

enum EmrAvailability {
    NOW = 'NOW',
    NOT_PLANNED = 'NOT_PLANNED',
}

export const DigitalFulfillmentModal = (props: {
    isOpen: boolean;
    setIsOpen: (value: boolean) => void;
}): JSX.Element => {
    const { user } = useUser();
    const [emrType, setEmrType] = useState<string | undefined>(undefined);
    const [eligibility, setEligiblity] = useState<EmrAvailability | undefined>(undefined);
    const [showOtherField, setShowOtherField] = useBoolean(false);
    const [showEnvironmentDropdown, setShowEnvironmentDropdown] = useBoolean(false);
    const [emrEnvironment, setEmrEnvironment] = useState<EmrEnvironment | undefined>(undefined);
    const utils = trpc.useUtils();
    const [hasNotSeenDfModal, setHasNotSeenDfModal] = useLocalStorage('rm-df-modal-open_v2', true);
    const { data: addressFeedbackData } = trpc.tenant.getPendingAddressFeedback.useQuery();
    const { data: dfEligibility } = trpc.tenant.getDfEligibility.useQuery(undefined, {
        staleTime: 5 * 60 * 10000, // 50 minutes
        cacheTime: 10 * 60 * 10000, // 100 minutes
    });
    const [emrOptions, setEmrOptions] = useState<{ value: any; label: any }[]>([]);
    const { data: tenantData, isLoading: isTenantDataLoading } = trpc.tenant.getTenant.useQuery(undefined, {
        staleTime: Infinity, // ✅ Data is always considered fresh
        cacheTime: Infinity, // ✅ Cache never garbage-collected
        refetchOnMount: false, // 🚫 Don't re-fetch on component remount
        refetchOnWindowFocus: false, // 🚫 Don't re-fetch when switching tabs
        refetchOnReconnect: false, // 🚫 Don't re-fetch when network reconnects
    });
    const agreementModalContext = useContext(AgreementModalContext);
    const isEMREligible = isTenantDataLoading
        ? false
        : (tenantData?.digitalMetadata?.isLiveDigitalFulfillment === false &&
              tenantData?.digitalMetadata?.eligibility === true) ??
          false;

    useEffect(() => {
        if (dfEligibility) {
            const uniqueEmrOptions = [...new Set(dfEligibility.map(emr => emr.emrName))].sort();
            setEmrOptions(uniqueEmrOptions.map(emr => ({ value: emr, label: emr })));
        }
    }, [dfEligibility]);

    const toast = useToast();

    // Eligible request counts over the trailing 12 months.
    const { data: numRequests } = trpc.requestManager.rmRouter.getDigitalEligibleRequestCounts.useQuery();
    const { data: tenantSites } = trpc.tenant.getTenantSites.useQuery(undefined, {
        staleTime: Infinity, // ✅ Data is always considered fresh
        cacheTime: Infinity, // ✅ Cache never garbage-collected
        refetchOnMount: false, // 🚫 Don't re-fetch on component remount
        refetchOnWindowFocus: false, // 🚫 Don't re-fetch when switching tabs
        refetchOnReconnect: false, // 🚫 Don't re-fetch when network reconnects
    });

    const processEmrInput = (val: string): void => {
        setEmrType(val);
        if (dfEligibility) {
            const emrData = dfEligibility.filter(emr => emr.emrName === val);
            if (emrData) {
                const emrEnvironments = emrData.map(emr => emr.emrEnvironment).filter(env => env !== null);
                if (emrEnvironments.length > 1) {
                    setShowEnvironmentDropdown.on();
                } else if (emrEnvironments.length === 1) {
                    if (emrEnvironments[0]) {
                        setEmrEnvironment(emrEnvironments[0]);
                    }
                }
            }
        }
    };

    const onOtherFieldChange = (val: string): void => {
        setEligiblity(undefined);
        setShowEnvironmentDropdown.off();
        setEmrEnvironment(undefined);
        processEmrInput(val);
    };

    const onSelectChange = (val: string): void => {
        setEligiblity(undefined);
        setShowEnvironmentDropdown.off();
        setEmrEnvironment(undefined);
        if (val === 'Other') {
            setEmrType(undefined);
            setShowOtherField.on();
        } else {
            setShowOtherField.off();
            processEmrInput(val);
        }
    };

    const onCheckEligibility = async (): Promise<void> => {
        if (!dfEligibility) {
            return;
        }
        const emrData = dfEligibility.find(emr => emr.emrName === emrType);
        let eligibility: EmrAvailability | undefined;

        if (!emrData) {
            eligibility = EmrAvailability.NOT_PLANNED;
        } else {
            if (emrData.isEligible) {
                eligibility = EmrAvailability.NOW;
            } else {
                eligibility = EmrAvailability.NOT_PLANNED;
            }
        }
        setEligiblity(eligibility);

        if (user?.tenantUUID) {
            const fulfillmentMethod = emrData?.emrMethod == null ? undefined : emrData?.emrMethod;
            await tRPCProxyClient.tenant.patchOwnTenant.mutate({
                isLiveDigitalFulfillment: false,
                emr: emrType,
                emrEnvironment: emrEnvironment,
                fulfillmentMethod: fulfillmentMethod,
            });
            await tRPCProxyClient.autofulfill.updateOnboardingStatus.mutate({
                status: NonEmbeddedImplementationStatus.EMR_SUBMITTED,
            });

            toast({
                title: 'EMR submitted successfully!',
                status: 'success',
                duration: 2000,
                isClosable: true,
            });

            if (eligibility === EmrAvailability.NOW && numRequests && numRequests.eligible >= 1000) {
                let states: string[] = [];
                if (addressFeedbackData) {
                    states = states.concat(
                        addressFeedbackData
                            .filter(feedback => feedback.feedbackType === AddressAssociationFeedbackType.ADD)
                            .map(feedback => feedback.state)
                    );
                }
                if (tenantSites) {
                    states = states.concat(tenantSites.startEndBlock.map(row => row.state));
                }

                // dedup
                states = Array.from(new Set(states));

                // no need to await this as we don't want to block the user
                void sendSlackAlert(
                    SlackChannelEnum.NONEMBEDDED_DIGITAL_LEADS,
                    `:sparkles: :chloe-roar-intensifies: New RRA Lead :chloe-roar-intensifies: :sparkles: \n\n` +
                        `Tenant: ${user.tenantName}\n\n` +
                        `EMR: ${emrType}\n\n` +
                        `Environment: ${emrEnvironment}\n\n` +
                        `Digital Eligible Inventory (trailing 12 months): ${numRequests?.eligible}\n\n` +
                        `Address States: ${states.join(', ')}\n`
                );
            }
        }
    };

    const EligibleMessage = () => {
        return (
            <UiKit.Chakra.Box background="#E1F5EB" padding="12px" borderRadius="6px">
                <UiKit.Chakra.HStack alignItems={'start'}>
                    <UiKit.Icons.FontAwesomeIcon
                        icon={UiKit.Icons.FontAwesome.regular.faCircleCheck}
                        color="#289B67"
                        style={{ paddingTop: '2px' }}
                    />
                    <Text.Contrast
                        color="#1F7C53"
                        size={'xs'}
                        appearance={'medium'}
                        align={'left'}
                        weight="semibold"
                    >
                        Your EMR is eligible at this time! Next steps are available in the onboarding tracker
                        in the left sidebar.
                    </Text.Contrast>
                </UiKit.Chakra.HStack>
            </UiKit.Chakra.Box>
        );
    };

    const modalBody = (
        <UiKit.Chakra.VStack align={'stretch'}>
            <UiKit.Chakra.VStack align={'center'} textAlign={'center'}>
                <DigitalFulfillmentLogo />
                <UiKit.Text.Heading2 text="What is your EMR setup?" />
                <UiKit.Text.BodyRegular>
                    Depending on your EMR, up to 85% of your work (payer audits) could be completed using
                    AutoFulfill for free; Replacing the manual finding and uploading of patient records for
                    those request types.
                </UiKit.Text.BodyRegular>
            </UiKit.Chakra.VStack>
            {isEMREligible ? (
                <UiKit.Chakra.VStack align={'center'} textAlign={'center'}>
                    <EligibleMessage />
                </UiKit.Chakra.VStack>
            ) : (
                <>
                    <UiKit.Chakra.VStack align={'stretch'}>
                        <UiKit.Text.BodyRegular>Select EMR</UiKit.Text.BodyRegular>
                        <SelectBoxWithHint
                            placeholder={'Search'}
                            textBefore={''}
                            value={emrType}
                            options={emrOptions}
                            onChange={onSelectChange}
                            width="100%"
                        />
                        {showOtherField && (
                            <InputWithHint
                                textBefore={'Enter EMR'}
                                value={emrType}
                                onChange={onOtherFieldChange}
                            />
                        )}

                        {showEnvironmentDropdown && (
                            <>
                                <UiKit.Text.BodyRegular>Select Environment</UiKit.Text.BodyRegular>
                                <UiKit.Form.RadioGroup
                                    value={emrEnvironment}
                                    onChange={val => {
                                        setEmrEnvironment(val as EmrEnvironment);
                                        setEligiblity(undefined);
                                    }}
                                    options={Object.values(EmrEnvironment)
                                        .filter(env => env !== EmrEnvironment.NOT_SURE)
                                        .map(env => ({
                                            value: env,
                                            text: String(env).replace('_', ' '),
                                            helpText: EmrEnvironmentHelpText[env],
                                            customTagText:
                                                env === EmrEnvironment.CLOUD ? 'Most Common' : undefined,
                                        }))}
                                />
                            </>
                        )}
                    </UiKit.Chakra.VStack>
                    {(eligibility === EmrAvailability.NOT_PLANNED || eligibility === EmrAvailability.NOW) && (
                        <UiKit.Chakra.VStack
                            align={'center'}
                            textAlign={'center'}
                            paddingTop={UiKit.consistency.largeBuffer}
                        >
                            {eligibility === EmrAvailability.NOT_PLANNED && (
                                <UiKit.Chakra.Box background="#F2F1EC" padding="12px" borderRadius="6px">
                                    <UiKit.Chakra.HStack alignItems={'start'}>
                                        <UiKit.Icons.Chakra.InfoOutlineIcon
                                            color="#8A8985"
                                            style={{ paddingTop: '2px' }}
                                        />
                                        <Text.Contrast
                                            color="#6E6D6A"
                                            size={'xs'}
                                            appearance={'medium'}
                                            align={'left'}
                                        >
                                            Your EMR is not eligible at this time; however, we will reach out
                                            once it is supported!
                                        </Text.Contrast>
                                    </UiKit.Chakra.HStack>
                                </UiKit.Chakra.Box>
                            )}
                            {eligibility === EmrAvailability.NOW && <EligibleMessage />}
                        </UiKit.Chakra.VStack>
                    )}
                    <Text.Contrast size={'xs'} appearance={'minimum'}>
                        Need help? Contact our support team via live chat under Help, or email us at
                        requestmanager@datavant.com.
                    </Text.Contrast>
                </>
            )}
        </UiKit.Chakra.VStack>
    );

    const getFooterButtons = (eligiblity: EmrAvailability | undefined) => {
        if (!eligiblity && !isEMREligible) {
            return (
                <>
                    <Button
                        onClick={onCheckEligibility}
                        data-pendo-id={'what-is-your-emr-setup'}
                        isDisabled={!emrType || (showEnvironmentDropdown && !emrEnvironment)}
                    >
                        Check eligibility
                    </Button>
                </>
            );
        } else if (eligiblity === EmrAvailability.NOW) {
            return (
                <>
                    <Button
                        onClick={() => {
                            void utils.tenant.getTenant.invalidate();
                            props.setIsOpen(false);
                            agreementModalContext.setIsOpen(true);
                        }}
                        buttonAppearance={'solid'}
                    >
                        Continue Setup
                    </Button>
                </>
            );
        } else {
            return (
                <>
                    <Button
                        onClick={() => {
                            void utils.tenant.getTenant.invalidate();
                            props.setIsOpen(false);

                            // Reload the page to show the updated onboarding tracker
                            window.location.reload();
                        }}
                        intent={'neutral'}
                        buttonAppearance={'ghost'}
                    >
                        Return to Request Manager
                    </Button>
                </>
            );
        }
    };

    const modalFooter = <UiKit.Chakra.HStack>{getFooterButtons(eligibility)}</UiKit.Chakra.HStack>;

    return (
        <UiKit.Modal
            bodyFragment={modalBody}
            footerFragment={modalFooter}
            title="AutoFulfill Eligible"
            onClose={() => {
                void utils.tenant.getTenant.invalidate();
                props.setIsOpen(false);
                setHasNotSeenDfModal(false);
            }}
            isOpen={props.isOpen}
            size="xl"
        />
    );
};
